import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@/hooks/useLanguage'
import { t } from '@/utils/i18n'
import { formatCurrency } from '@/utils/currency'
import { Dream } from '@/store/types/dream.types'
import Card from '@/components/common/Card/Card'
import { IconClose } from '@/components/icons/Icons'
import './DreamCard.css'

interface DreamCardProps {
  dream: Dream
  onEdit?: (dream: Dream) => void
  onDelete?: (dreamId: string) => void
  onAccount?: (dreamId: string) => void
}

function DreamCard({ dream, onEdit, onDelete }: DreamCardProps) {
  const navigate = useNavigate()
  const { language } = useLanguage()

  const handleClick = () => {
    navigate(`/dreams/${dream.dreamId}`)
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(dream)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (onDelete && dream.dreamId) {
      onDelete(dream.dreamId)
    }
  }

  const rawCompletion = dream.completionRate ?? 0
  // normalize: backend may send 25.0 meaning 25%, or 0.25 meaning 25%
  const completionRate = rawCompletion > 1 ? rawCompletion / 100 : rawCompletion

  const getStatusColor = () => {
    if (completionRate >= 1) return 'var(--color-status-completed)'
    if (completionRate > 0) return 'var(--color-status-in-progress)'
    return 'var(--color-status-preparing)'
  }

  const getStatusText = () => {
    if (completionRate >= 1) return t('common.completed', language)
    if (completionRate > 0) return t('common.inProgress', language)
    return t('common.preparing', language)
  }

  return (
    <Card className="dream-card" onClick={handleClick}>
      <div className="dream-card__header">
        <div className="dream-card__title-wrap">
          <h3 className="dream-card__title">{dream.dreamName}</h3>
        </div>
        <div className="dream-card__header-actions">
          <button
            type="button"
            className="dream-card__icon-btn"
            onClick={handleEdit}
            aria-label={t('common.edit', language)}
            title={t('common.edit', language)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            type="button"
            className="dream-card__close"
            onClick={handleDelete}
            aria-label={t('common.close', language)}
          >
            <IconClose size={18} />
          </button>
        </div>
      </div>
      <p className="dream-card__desc">{dream.dreamDesc}</p>
      <div className="dream-card__info">
        <div className="dream-card__date">
          {dream.startDate} ~ {dream.endDate}
        </div>
        <div className="dream-card__budget">
          <div className="dream-card__budget-item">
            <span className="dream-card__budget-label">{t('dreams.goalBudget', language)}</span>
            <span className="dream-card__budget-value dream-card__budget-value--goal">
              {formatCurrency(dream.budget, language)}
            </span>
          </div>
          <div className="dream-card__budget-item">
            <span className="dream-card__budget-label">{t('dreams.itemTotalBudget', language)}</span>
            <span className="dream-card__budget-value dream-card__budget-value--item">
              {formatCurrency(dream.totalItemPrice || 0, language)}
            </span>
          </div>
        </div>
      </div>
      <div className="dream-card__progress">
        <div className="dream-card__progress-info">
          <span
            className="dream-card__status"
            style={{ color: getStatusColor() }}
          >
            {getStatusText()}
          </span>
          <span className="dream-card__rate">
            {Math.round(completionRate * 100)}%
          </span>
        </div>
        <div className="dream-card__progress-bar">
          <div
            className="dream-card__progress-fill"
            style={{
              width: `${completionRate * 100}%`,
              backgroundColor: getStatusColor(),
            }}
          />
        </div>
      </div>
    </Card>
  )
}

export default DreamCard

